/**
 * 日次締め作業(2026-09-24 発注者指示。D-147)。締めたい日を選んで ①〜④ の順に進める。
 *  ① 各社の納品件数(会社マスタの曜日別予定を初期値に出す)
 *  ② 人件費(稼働時間・後処理・休憩・離席 = OPレポートから自動 ／ 稼働時間・休憩時間 = 手入力で上書き)
 *  ③ 交通費(マスタの交通費・その日の稼働時間・ルール適用後の自動計算・手入力)
 *  ④ 出勤表の作成(既定は翌日。日付を変えられる)
 * **ステップごとに保存**でき、順番どおりでなく行き来できる(途中保存)。URL は `?d=締める日&step=1..4`。
 * ④の出勤表は既存の出勤表(AttendanceTab)をそのまま使う(その画面は `?date=` を見る)。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@core/auth/AuthProvider';
import NumInput from '@core/components/NumInput';
import { useToast } from '@core/components/Toast';
import { IconChevronLeft, IconChevronRight } from '@core/components/icons';
import FilterBar from '@core/components/ui/FilterBar';
import PageHeader from '@core/components/ui/PageHeader';
import Panel from '@core/components/ui/Panel';
import StatusBadge from '@core/components/ui/StatusBadge';
import { todayStr, weekdayLabel } from '@core/lib/date';
import { commuteAmount } from '@core/lib/commute';
import { round05 } from '@core/import/buildDailySummary';
import {
  DELIVERY_STYLES,
  DELIVERY_STYLE_LABELS,
  commuteOn,
  plannedDeliveryOn,
  type Company,
  type DailySummary,
  type DateStr,
  type Delivery,
  type DeliveryDetail,
  type DeliveryStyle,
  type Operator,
  WORK_STYLE_LABELS,
} from '@core/lib/types';
import { emptyDeliveryDetail } from "@core/lib/types";
import { fetchCompanies } from '@core/masters/companyRepo';
import { fetchOperators } from '@core/masters/operatorRepo';
import { fetchKintai, saveKintaiCheck, type KintaiEntry } from '@core/import/importRepo';
import AttendanceTab from '@core/staff/AttendanceTab';
import { formatCount, formatCurrency, formatDecimal, formatDuration } from '@core/utils/formatters';
import { addDays } from './calc';
import { fetchDelivery, saveDelivery } from './deliveryRepo';
import { fetchDailySummary } from './dashboardRepo';

/** ②③のカードの分け方(2026-09-25 発注者指示) */
type GroupKey = 'shukkin' | 'zaitaku_jikyu' | 'zaitaku_buai';
/** その人がどのカードか */
function groupOf(r: { work_style: string; pay_type: string }): GroupKey | null {
  // 管理者の報酬は売上原価に入れない(D-28)ので人件費・交通費には出さない(2026-09-25 発注者指示)
  if (r.work_style === 'kanri') return null;
  if (r.work_style === 'zaitaku') return r.pay_type === 'commission' ? 'zaitaku_buai' : 'zaitaku_jikyu';
  return 'shukkin';
}

const GROUPS: { key: GroupKey; label: string; editable: boolean }[] = [
  { key: 'shukkin', label: '出勤', editable: true },
  { key: 'zaitaku_jikyu', label: '在宅／時給', editable: true },
  { key: 'zaitaku_buai', label: '在宅／歩合', editable: false },
];

const STEPS = [
  { key: 1, label: '① 納品件数' },
  { key: 2, label: '② 人件費' },
  { key: 3, label: '③ 交通費' },
  { key: 4, label: '④ 出勤表' },
] as const;

/** ②③で1行になる人(出勤・タイミー。OPレポートのある人) */
type PersonRow = {
  bb_id: string;
  op_id: string;
  name: string;
  work_style: 'shinagawa' | 'timee' | 'zaitaku' | 'kanri';
  pay_type: 'hourly' | 'commission';
  /** 出勤・単発だけ 勤怠の手修正(②③の入力)ができる。在宅・管理は BlueBean の稼働がそのまま */
  editable: boolean;
  /** OPレポートの自動値 */
  login_h: number;
  /** BB稼働(受付＋通話＋後処理)。在宅・管理の実稼働はこれ */
  bb_h: number;
  after_h: number;
  break_h: number;
  away_h: number;
  /** いまの確定値(手修正があればその値) */
  shift_hours: number;
  break_hours: number;
  worked_hours: number;
  manual: boolean;
  commute_auto: number;
  commute_master: number;
};

export default function CloseDayPage() {
  const toast = useToast();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const date = (params.get('d') ?? todayStr()) as DateStr;
  const step = Number(params.get('step') ?? '1') || 1;
  const setStep = (n: number) => {
    const p = new URLSearchParams(params);
    p.set('step', String(n));
    // ④は翌日の出勤表を作るので、出勤表が見る `date` を翌日にしておく
    if (n === 4 && !p.get('date')) p.set('date', addDays(date, 1));
    setParams(p, { replace: true });
  };
  const setDate = (d: DateStr) => {
    const p = new URLSearchParams(params);
    p.set('d', d);
    p.delete('date');
    setParams(p, { replace: true });
  };

  const [companies, setCompanies] = useState<Company[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [summary, setSummary] = useState<DailySummary | null>(null);
  const [delivery, setDelivery] = useState<Delivery | null>(null);
  const [details, setDetails] = useState<Record<string, DeliveryDetail>>({});
  const [rows, setRows] = useState<PersonRow[]>([]);
  const [edit, setEdit] = useState<Record<string, { shift: string; brk: string; commute: string }>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cs, ops, sm, dv, kintai] = await Promise.all([fetchCompanies(), fetchOperators(), fetchDailySummary(date), fetchDelivery(date), fetchKintai(date)]);
      setCompanies(cs);
      setOperators(ops);
      setSummary(sm);
      setDelivery(dv);
      setDetails({ ...(dv?.details ?? {}) });
      // ②③の行(出勤・タイミー。1人1行にまとめる)
      const byBb = new Map<string, PersonRow>();
      for (const s of sm?.staff ?? []) {
        if (byBb.has(s.bb_id)) continue;
        const op = ops.find((o: Operator) => o.id === s.op_id);
        const master = op && s.work_style === 'shinagawa' ? commuteOn(op, date) : 0;
        // 歩合は件数で報酬が決まるので時間の入力はしない。それ以外(出勤・単発・在宅時給・管理)は入力できる(2026-09-25)
        const editable = !(s.work_style === 'zaitaku' && s.pay_type === 'commission');
        byBb.set(s.bb_id, {
          bb_id: s.bb_id,
          op_id: s.op_id,
          name: s.name,
          work_style: s.work_style,
          pay_type: s.pay_type,
          editable,
          login_h: (s.login_sec ?? 0) / 3600,
          bb_h: ((s.reception_sec ?? 0) + (s.talk_sec ?? 0) + (s.after_sec ?? 0)) / 3600,
          after_h: (s.after_sec ?? 0) / 3600,
          break_h: (s.break_sec ?? 0) / 3600,
          away_h: (s.away_sec ?? 0) / 3600,
          shift_hours: s.shift_hours ?? 0,
          break_hours: s.break_hours ?? 0,
          worked_hours: s.worked_hours ?? 0,
          manual: s.hours_source === 'manual',
          commute_auto: s.commute ?? 0,
          commute_master: master,
        });
      }
      const order = (r: PersonRow) => (r.work_style === 'shinagawa' ? 0 : r.work_style === 'timee' ? 1 : r.work_style === 'zaitaku' ? (r.pay_type === 'commission' ? 3 : 2) : 4);
      const list = [...byBb.values()].sort((a, b) => order(a) - order(b) || a.name.localeCompare(b.name, 'ja'));
      setRows(list);
      setEdit(
        Object.fromEntries(
          list.map((r) => {
            const k = kintai.get(r.bb_id);
            const r2 = (v: number) => String(Math.round(v * 100) / 100); // 5.39999999 のような端数を出さない
            return [r.bb_id, { shift: k ? r2(k.shift_hours) : '', brk: k ? r2(k.break_hours) : '', commute: typeof k?.commute_manual === 'number' ? r2(k.commute_manual) : '' }];
          }),
        ),
      );
    } catch (e) {
      toast(e instanceof Error ? e.message : '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [date, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  /** ①に出す会社: 会社マスタのステータスが「停止」でない会社だけ(2026-09-25 発注者指示) */
  const active = useMemo(() => companies.filter((c) => c.status === 'active'), [companies]);
  /**
   * ②③は **出勤／在宅・時給／在宅・歩合／管理** のカードに分け、カードごとに出す・出さないを切り替える(2026-09-25 発注者指示)。
   * 既定: ②は在宅/歩合だけ閉じる ／ ③は出勤だけ開く
   */
  const [payOpen, setPayOpen] = useState<Record<GroupKey, boolean>>({ shukkin: true, zaitaku_jikyu: true, zaitaku_buai: false });
  const [commuteOpen, setCommuteOpen] = useState<Record<GroupKey, boolean>>({ shukkin: true, zaitaku_jikyu: false, zaitaku_buai: false });
  const rowsOf = (key: GroupKey) => rows.filter((r) => groupOf(r) === key);
  const dateLabel = `${date.slice(5, 7)}/${date.slice(8)}${weekdayLabel(date)}`;
  const hasOpReport = !!summary?.sources?.op_report;

  // ---- ① 納品件数 ----
  const detailOf = (id: string): DeliveryDetail => details[id] ?? emptyDeliveryDetail();
  const setStyle = (id: string, style: DeliveryStyle, v: string) =>
    setDetails((prev) => {
      const d = { ...(prev[id] ?? emptyDeliveryDetail()) };
      d.by_style = { ...d.by_style, [style]: Math.max(0, Number(v || 0)) };
      return { ...prev, [id]: d };
    });
  const totalOf = (id: string) => DELIVERY_STYLES.reduce((a, k) => a + (detailOf(id).by_style[k] ?? 0), 0);
  const plannedOf = (c: Company) => plannedDeliveryOn(c, date);
  /** 予定を「出勤」の欄に入れる(内訳が分かれる場合は手で直す) */
  const applyPlan = (c: Company) => {
    const n = plannedOf(c);
    if (n === null) return;
    setStyle(c.id, 'shinagawa', String(n));
  };
  const applyAllPlans = () => {
    for (const c of active) if (plannedOf(c) !== null && totalOf(c.id) === 0) applyPlan(c);
    toast('予定の件数を入れました(内訳は必要に応じて直してください)');
  };

  async function saveStep1(next?: number) {
    if (!user) return;
    setBusy(true);
    try {
      await saveDelivery(date, details, delivery?.note ?? '', user.id, delivery);
      toast('納品件数を保存しました');
      await load();
      if (next) setStep(next);
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  // ---- ②③ 人件費・交通費(勤怠の手修正として保存) ----
  async function saveKintai(next?: number) {
    if (!user) return;
    if (!hasOpReport) {
      toast('この日は OPレポートが未取込です(取り込んでから入力してください)');
      return;
    }
    setBusy(true);
    try {
      const entries: KintaiEntry[] = [];
      for (const r of rows.filter((x) => x.editable)) {
        const e = edit[r.bb_id] ?? { shift: '', brk: '', commute: '' };
        const hasAny = e.shift.trim() !== '' || e.brk.trim() !== '' || e.commute.trim() !== '';
        if (!hasAny) {
          if (r.manual) entries.push({ bb_id: r.bb_id, op_id: r.op_id, value: null });
          continue;
        }
        const shift = e.shift.trim() === '' ? r.shift_hours : Number(e.shift);
        const brk = e.brk.trim() === '' ? r.break_hours : Number(e.brk);
        if (!Number.isFinite(shift) || shift < 0 || !Number.isFinite(brk) || brk < 0) {
          toast(`${r.name} の時間は0以上の数字で入れてください`);
          setBusy(false);
          return;
        }
        const commute = e.commute.trim() === '' ? null : Number(e.commute);
        if (commute !== null && (!Number.isFinite(commute) || commute < 0)) {
          toast(`${r.name} の交通費は0以上の数字で入れてください`);
          setBusy(false);
          return;
        }
        entries.push({ bb_id: r.bb_id, op_id: r.op_id, value: { shift_hours: shift, break_hours: brk, note: '', commute_manual: commute } });
      }
      await saveKintaiCheck({ date, entries, operators, companies, userId: user.id, userName: user.name ?? null });
      toast('保存しました');
      await load();
      if (next) setStep(next);
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  const setEditField = (bb: string, key: 'shift' | 'brk' | 'commute', v: string) =>
    setEdit((prev) => ({ ...prev, [bb]: { ...(prev[bb] ?? { shift: '', brk: '', commute: '' }), [key]: v } }));

  /** ③の自動計算(②で入れた稼働時間に交通費のルールを当てたもの) */
  const commuteAutoOf = (r: PersonRow) => {
    const e = edit[r.bb_id];
    const shift = e && e.shift.trim() !== '' ? Number(e.shift) : r.shift_hours;
    const brk = e && e.brk.trim() !== '' ? Number(e.brk) : r.break_hours;
    const worked = Math.max(0, Math.round((shift - brk) * 100) / 100);
    return { worked, amount: commuteAmount(r.commute_master, worked, 0) };
  };

  return (
    <div className="screen full">
      <PageHeader title="日次締め作業" meta={`${date}${weekdayLabel(date)}`} />
      <FilterBar label="締める日">
        <span className="ui-date-nav">
          <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setDate(addDays(date, -1))} aria-label="前の日">
            <IconChevronLeft size={16} />
          </button>
          <input type="date" className="ui-input" aria-label="日付" value={date} onChange={(e) => e.target.value && setDate(e.target.value as DateStr)} />
          <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setDate(addDays(date, 1))} aria-label="次の日">
            <IconChevronRight size={16} />
          </button>
        </span>
        <span className="ui-period-label">{dateLabel}</span>
        <span className="close-steps">
          {STEPS.map((s) => (
            <button key={s.key} type="button" className={`ui-btn ui-btn-sm ${step === s.key ? 'ui-btn-primary' : 'ui-btn-secondary'}`} onClick={() => setStep(s.key)}>
              {s.label}
            </button>
          ))}
        </span>
        {!hasOpReport && <StatusBadge kind="notEntered" label="OPレポート未取込" />}
      </FilterBar>

      {loading ? (
        <div className="ui-panel">
          <div className="ui-empty">読み込み中…</div>
        </div>
      ) : step === 1 ? (
        <Panel
          title={`① 各社の納品件数(${dateLabel})`}
          meta={`${formatCount(active.length, { unit: '社' })} ／ 合計 ${formatCount(active.reduce((a, c) => a + totalOf(c.id), 0), { unit: '件' })}`}
          note="**予定**は会社マスタの「納品件数の予定(曜日別)」です。内訳(出勤/歩合/時給)を入れると合計になります。**会社マスタのステータスが「停止」の会社は出ません**。保存してから次へ進んでください(あとから戻って直せます)"
          actions={
            <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={applyAllPlans}>
              予定をまとめて入れる
            </button>
          }
        >
          <div className="close-table-wrap">
            <table className="fit-table no-stack close-table">
              <thead>
                <tr>
                  <th style={{ width: 200 }}>会社</th>
                  <th className="num" style={{ width: 64 }}>予定</th>
                  {DELIVERY_STYLES.map((k) => (
                    <th key={k} className="num" style={{ width: 96 }}>
                      {DELIVERY_STYLE_LABELS[k]}
                    </th>
                  ))}
                  <th className="num" style={{ width: 70 }}>合計</th>
                  <th className="num" style={{ width: 80 }}>単価</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {active.map((c) => {
                  const planned = plannedOf(c);
                  return (
                    <tr key={c.id}>
                      <td>{c.name}</td>
                      <td className="num">
                        {planned === null ? (
                          <span className="cell-na">—</span>
                        ) : (
                          <button type="button" className="txn-link" onClick={() => applyPlan(c)} title="この件数を入れる">
                            {formatCount(planned)}
                          </button>
                        )}
                      </td>
                      {DELIVERY_STYLES.map((k) => (
                        <td key={k} className="num">
                          <NumInput className="close-num" value={String(detailOf(c.id).by_style[k] ?? 0)} onChange={(v) => setStyle(c.id, k, v)} />
                        </td>
                      ))}
                      <td className="num">
                        <b>{formatCount(totalOf(c.id))}</b>
                      </td>
                      <td className="num cell-na">{formatCurrency(c.unit_price)}</td>
                      <td />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="modal-actions" style={{ justifyContent: 'flex-start' }}>
            <button type="button" className="ui-btn ui-btn-secondary" disabled={busy} onClick={() => void saveStep1()}>
              保存する
            </button>
            <button type="button" className="ui-btn ui-btn-primary" disabled={busy} onClick={() => void saveStep1(2)}>
              保存して次へ(② 人件費)
            </button>
          </div>
        </Panel>
      ) : step === 2 ? (
        <>
        {GROUPS.map((g) => (
        <Panel
          key={g.key}
          title={`② 人件費 ／ ${g.label}(${dateLabel})`}
          meta={formatCount(rowsOf(g.key).length, { unit: '名' })}
          actions={
            <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" aria-expanded={payOpen[g.key]} onClick={() => setPayOpen((p) => ({ ...p, [g.key]: !p[g.key] }))}>
              {payOpen[g.key] ? '− 閉じる' : '＋ 開く'}
            </button>
          }
          note={!payOpen[g.key] ? undefined : g.editable ? '「稼働時間」「稼働時間(h)」は**OPレポートの自動値**です。右の2つに入れると**その値で計算し直します**(空なら自動値のまま)' : '**歩合は件数で報酬が決まる**ので、時間の入力はありません(稼働は BlueBean の記録)'}
        >
          {!payOpen[g.key] ? (
            <div className="ui-hint">閉じています(「＋ 開く」で表示)</div>
          ) : rowsOf(g.key).length === 0 ? (
            <div className="ui-empty">{hasOpReport ? 'この日の稼働がありません' : 'OPレポートを取り込むと、ここに出ます'}</div>
          ) : (
            <>
              <div className="close-table-wrap">
                <table className="fit-table no-stack close-table">
                  <thead>
                    <tr>
                      <th style={{ width: 210 }}>スタッフ</th>
                      <th className="num">稼働時間(自動)</th>
                      <th className="num">後処理</th>
                      <th className="num">休憩</th>
                      <th className="num">離席</th>
                      <th className="num">稼働時間</th>
                      <th className="num">稼働時間(h)</th>
                      <th className="num">稼働時間(入力)</th>
                      <th className="num">休憩時間(入力)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rowsOf(g.key).map((r) => {
                      const e = edit[r.bb_id] ?? { shift: '', brk: '', commute: '' };
                      // 「稼働時間」はOPレポートの自動値(手入力は当てない。2026-09-25 発注者指示)
                      // 出勤・単発は 拘束(ログイン)−休憩、在宅・管理は BB稼働(受付＋通話＋後処理)。どちらも**OPレポートの自動値**
                      const autoWorked =
                        r.work_style === 'shinagawa' || r.work_style === 'timee'
                          ? Math.max(0, Math.round((round05(r.login_h) - round05(r.break_h + r.away_h)) * 100) / 100)
                          : Math.round(r.bb_h * 100) / 100;
                      return (
                        <tr key={r.bb_id}>
                          <td>
                            {r.name}
                            <span className="ui-hint">
                              {' '}
                              {r.bb_id}・{WORK_STYLE_LABELS[r.work_style]}
                            </span>
                            {r.manual && <span className="mark-edited">手</span>}
                          </td>
                          <td className="num">{formatDuration(r.login_h * 3600)}</td>
                          <td className="num">{formatDuration(r.after_h * 3600)}</td>
                          <td className="num">{formatDuration(r.break_h * 3600)}</td>
                          <td className="num">{formatDuration(r.away_h * 3600)}</td>
                          <td className="num">
                            <b>{formatDuration(autoWorked * 3600)}</b>
                          </td>
                          <td className="num">
                            <b>{formatDecimal(autoWorked)}</b>h
                          </td>
                          <td className="num">
                            {r.editable ? (
                              <NumInput decimal className="close-num" value={e.shift} onChange={(v) => setEditField(r.bb_id, 'shift', v)} placeholder={String(Math.round((autoWorked + (r.break_hours || 0)) * 100) / 100)} />
                            ) : (
                              <span className="cell-na">—</span>
                            )}
                          </td>
                          <td className="num">
                            {r.editable ? (
                              <NumInput decimal className="close-num" value={e.brk} onChange={(v) => setEditField(r.bb_id, 'brk', v)} placeholder={String(r.break_hours || 0)} />
                            ) : (
                              <span className="cell-na">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Panel>
        ))}
        <div className="modal-actions" style={{ justifyContent: 'flex-start' }}>
          <button type="button" className="ui-btn ui-btn-secondary" disabled={busy} onClick={() => void saveKintai()}>
            保存する
          </button>
          <button type="button" className="ui-btn ui-btn-primary" disabled={busy} onClick={() => void saveKintai(3)}>
            保存して次へ(③ 交通費)
          </button>
        </div>
        </>
      ) : step === 3 ? (
        <>
        {GROUPS.map((g) => (
        <Panel
          key={g.key}
          title={`③ 交通費 ／ ${g.label}(${dateLabel})`}
          meta={formatCount(rowsOf(g.key).length, { unit: '名' })}
          actions={
            <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" aria-expanded={commuteOpen[g.key]} onClick={() => setCommuteOpen((p) => ({ ...p, [g.key]: !p[g.key] }))}>
              {commuteOpen[g.key] ? '− 閉じる' : '＋ 開く'}
            </button>
          }
          note={!commuteOpen[g.key] ? undefined : g.key === 'shukkin' ? '**自動計算**は②で入れた稼働時間に交通費のルール(6時間未満=0 / 6〜8時間=30% / 8時間以上=50%)を当てた金額です。**手入力**を入れるとその金額になります(空なら自動計算)' : '**交通費は出勤だけ**が対象です(P-38)'}
        >
          {!commuteOpen[g.key] ? (
            <div className="ui-hint">閉じています(「＋ 開く」で表示)</div>
          ) : rowsOf(g.key).length === 0 ? (
            <div className="ui-empty">対象の人がいません</div>
          ) : (
            <>
              <div className="close-table-wrap">
                <table className="fit-table no-stack close-table">
                  <thead>
                    <tr>
                      <th style={{ width: 210 }}>スタッフ</th>
                      <th className="num">マスタの交通費(往復)</th>
                      <th className="num">その日の稼働時間</th>
                      <th className="num">交通費(自動計算)</th>
                      <th className="num">交通費(手入力)</th>
                      <th className="num">この日の交通費</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rowsOf(g.key).map((r) => {
                      const e = edit[r.bb_id] ?? { shift: '', brk: '', commute: '' };
                      const { worked, amount } = commuteAutoOf(r);
                      const fixed = e.commute.trim() === '' ? amount : Number(e.commute || 0);
                      return (
                        <tr key={r.bb_id}>
                          <td>
                            {r.name}
                            <span className="ui-hint">
                              {' '}
                              {r.bb_id}・{WORK_STYLE_LABELS[r.work_style]}
                            </span>
                          </td>
                          <td className="num cell-na">{r.commute_master > 0 ? formatCurrency(r.commute_master) : '対象外'}</td>
                          <td className="num">{formatDecimal(worked)}h</td>
                          <td className="num">{formatCurrency(amount)}</td>
                          <td className="num">
                            {r.editable ? (
                              <NumInput className="close-num" value={e.commute} onChange={(v) => setEditField(r.bb_id, 'commute', v)} placeholder="自動" />
                            ) : (
                              <span className="cell-na">—</span>
                            )}
                          </td>
                          <td className="num">
                            <b>{formatCurrency(fixed)}</b>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Panel>
        ))}
        <div className="modal-actions" style={{ justifyContent: 'flex-start' }}>
          <button type="button" className="ui-btn ui-btn-secondary" disabled={busy} onClick={() => void saveKintai()}>
            保存する
          </button>
          <button type="button" className="ui-btn ui-btn-primary" disabled={busy} onClick={() => void saveKintai(4)}>
            保存して次へ(④ 出勤表)
          </button>
        </div>
        </>
      ) : (
        <>
          <AttendanceTab />
        </>
      )}
    </div>
  );
}
